import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Drawer,
  Tooltip,
  CircularProgress,
  useTheme,
  IconButton,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon
} from '@mui/material';
import FiltersSidebar from '../components/assets/FiltersSidebar';
import AssetOperations from '../components/assets/AssetOperations';
import GenerateCsvModal from '../components/csv/GenerateCsvModal';
import SyncIcon from '@mui/icons-material/Sync';
import useMediaQuery from '@mui/material/useMediaQuery';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RoleBasedAccess, { userHasAccess } from '../services/RoleBasedAccess';
import PaginationFooter from '../components/assets/PaginationFooter';
import { AssetsProvider, useAssetsContext } from '../context/AssetsContext';
import { hasActiveAssetFilters } from '../components/assets/config';
import AssetCardList from '../components/assets/AssetCardList';
import AssetDesktopTable from '../components/assets/AssetDesktopTable';
import AddAssetModal from '../components/asset/AddAssetModal/ModalContainer'
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import { useSession } from '../context/SessionContext';
import { alpha } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';

const Assets: React.FC = () => {
  return (
    <AssetsProvider>
      <AssetsContent />
    </AssetsProvider>
  );
};

const AssetsContent: React.FC = () => {
  const theme = useTheme();
  const accent = theme.palette.status.accent;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const { user, selectedSystem, systems, selectSystem } = useSession();
  const [searchParams] = useSearchParams();
  const urlSystemAppliedRef = useRef(false);
  const assetsState = useAssetsContext();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [pollingEnabled, setPollingEnabled] = useState(false);
  const [isAddAssetModalOpen, setIsAddAssetModalOpen] = useState(false);
  const [isSpeedDialOpen, setIsSpeedDialOpen] = useState(false);
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);


  useEffect(() => {
    if (urlSystemAppliedRef.current) return;
    if (!systems.length) return;

    const urlSystemCode = searchParams.get('system');
    if (!urlSystemCode) {
      urlSystemAppliedRef.current = true;
      return;
    }

    const normalizedUrlCode = urlSystemCode.replace(/U\d{2}$/, '');
    const target = systems.find((system) => system.systemCode === normalizedUrlCode) || null;

    if (target && target.systemCode !== selectedSystem?.systemCode) {
      selectSystem(target);
    }

    urlSystemAppliedRef.current = true;
  }, [searchParams, selectedSystem?.systemCode, selectSystem, systems]);

  useEffect(() => {
    if (pollingEnabled) {
      assetsState.refetch(); // Fetch immediately on enable
      const interval = setInterval(() => {
        assetsState.refetch();
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [pollingEnabled, assetsState.refetch]);

  useEffect(() => {
    // Only set up listeners and timeouts if polling is active.
    if (pollingEnabled) {
      // Handler for tab visibility
      const handleVisibilityChange = () => {
        if (document.hidden) {
          console.log("Tab is hidden, disabling polling.");
          setPollingEnabled(false);
        }
      };

      // Set up a 10-minute timeout as a safety net (10 min * 60 sec/min * 1000 ms/sec)
      const pollingTimeout = setTimeout(() => {
        console.log("Polling timeout reached, disabling polling.");
        setPollingEnabled(false);
      }, 10 * 60 * 1000);

      // Add all event listeners
      document.addEventListener("visibilitychange", handleVisibilityChange);

      // The cleanup function is crucial. It runs when polling is turned off (either manually or by one of our handlers).
      return () => {
        console.log("Cleaning up inactivity listeners and timeout.");
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        clearTimeout(pollingTimeout); // <-- This prevents the timeout from firing if the user disables polling early.
      };
    }
  }, [pollingEnabled]);

  const filtersApplied = hasActiveAssetFilters(assetsState.filters);

  const sidebarWidth = isSidebarOpen
    ? (isMobile ? '70vw' : '280px')
    : (isMobile ? '12vw' : '60px');

  const handleAddAssetModalClose = () => {
    setIsAddAssetModalOpen(false); // Close the modal
    assetsState.refetch();      // Refetch the data
  };


  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 0, overflow: 'hidden' }}>
        {!isMobile && (
          <Box
            sx={{
              height: '100%',
              overflowY: 'auto',
              width: sidebarWidth,
              transition: 'width 0.3s ease-in-out',
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.text.primary,
              display: 'flex',
              flexDirection: 'column',
              px: isSidebarOpen ? 2 : 0,
              zIndex: 100,
              flexShrink: 0
            }}
          >
            {isSidebarOpen ? (
              <FiltersSidebar
                toggleSidebar={() => setIsSidebarOpen(false)}
              />
            ) : (
              <Box display="flex" flexDirection="column">
                <Tooltip title="Open Filters" placement="right" arrow>
                  <IconButton
                    onClick={() => setIsSidebarOpen(true)}
                    sx={{
                      m: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 5,
                      color: filtersApplied ? '#fff' : theme.palette.text.primary,
                      backgroundColor: filtersApplied ? accent : 'transparent',
                      '&:hover': {
                        backgroundColor: filtersApplied ? alpha(accent, 0.9) : theme.palette.action.hover,
                        borderColor: filtersApplied ? accent : theme.palette.text.primary,
                      },
                    }}
                  >
                    <FilterAltOutlinedIcon />
                  </IconButton>
                </Tooltip>


                <RoleBasedAccess allowedRoles={['Admin', 'Support', 'Auditor']}>
                  <Tooltip title="Add New Asset" placement="right" arrow>
                    <IconButton
                      onClick={() => setIsAddAssetModalOpen(true)}
                      sx={{
                        m: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 5,
                        color: theme.palette.text.primary,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                          borderColor: theme.palette.text.primary,
                        },
                      }}
                    >
                      <AddCircleOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </RoleBasedAccess>

                <Tooltip title="Generate CSV" placement="right" arrow>
                  <IconButton
                    onClick={() => setIsCsvModalOpen(true)} // It controls the parent's state
                    sx={{
                      m: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 5,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        borderColor: theme.palette.text.primary,
                      },
                    }}
                  >
                    <DescriptionIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Poll for new assets every 60 seconds" placement="right" arrow>
                  <IconButton
                    onClick={() => setPollingEnabled((prev) => !prev)}
                    sx={{
                      m: 1,
                      borderRadius: 5,
                      border: `1px solid ${theme.palette.divider}`,
                      color: pollingEnabled ? '#fff' : theme.palette.text.primary,
                      backgroundColor: pollingEnabled ? accent : 'transparent',
                      '&:hover': {
                        backgroundColor: pollingEnabled ? alpha(accent, 0.9) : theme.palette.action.hover,
                        borderColor: pollingEnabled ? accent : theme.palette.text.primary,
                      },
                    }}
                  >
                    {pollingEnabled ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SyncIcon />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        )}

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            padding: 1,
            color: 'text.primary',
            overflow: 'hidden'
          }}
        >

          {!selectedSystem ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', textAlign: 'center', px: 2 }}>
              <Box>
                <Box sx={{ fontSize: 18, fontWeight: 600, mb: 1 }}>
                  No systems assigned
                </Box>
                <Box sx={{ opacity: 0.8 }}>
                  Your account doesn’t have access to any systems.
                </Box>
                <Box sx={{ opacity: 0.8 }}>
                  Please select a system or contact support to get access.
                </Box>
              </Box>
            </Box>
          ) : assetsState.assets.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', textAlign: 'center', px: 2 }}>
              <Box>
                <Box sx={{ fontSize: 18, fontWeight: 600, mb: 1 }}>
                  No assets found
                </Box>
                <Box sx={{ opacity: 0.8 }}>
                  Try widening your date range or clearing filters.
                </Box>
                <Box sx={{ opacity: 0.8 }}>
                  Please check your filter settings.
                </Box>
              </Box>
            </Box>
          ) : (
            <>
              <Box sx={{
                flex: 1,
                overflowY: 'auto',
                paddingBottom: '20px',
                scrollbarWidth: 'none',        // Firefox
                '&::-webkit-scrollbar': {
                  display: 'none',             // Chrome, Safari
                },
              }}>
                <RoleBasedAccess allowedRoles={['Admin', 'Support', 'Auditor']}>
                  <AssetOperations />
                </RoleBasedAccess>
                {isMobile ? <AssetCardList /> : <AssetDesktopTable />}
              </Box>

              {/* Pagination + Results Per Page */}
              <PaginationFooter />
            </>
          )}
        </Box>

        {isMobile && (
          <>
            <SpeedDial
              ariaLabel="Asset Actions SpeedDial"
              sx={{ position: 'fixed', bottom: 16, right: 16 }}
              icon={<SpeedDialIcon icon={<FilterListIcon />} openIcon={<CloseIcon />} />}
              onClose={() => setIsSpeedDialOpen(false)}
              onOpen={() => setIsSpeedDialOpen(true)}
              open={isSpeedDialOpen}
              // Style the main FAB based on filtersApplied
              FabProps={{
                sx: {
                  backgroundColor: filtersApplied ? accent : '#000',
                  color: filtersApplied ? '#fff' : 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: filtersApplied ? alpha(accent, 0.9) : '#000',
                  },
                },
              }}
            >
              <SpeedDialAction
                key="Filters"
                icon={<FilterAltOutlinedIcon color="inherit" />}
                tooltipTitle="Filters"
                onClick={() => {
                  setIsSpeedDialOpen(false);
                  setIsFilterDrawerOpen(true);
                }}
              />

              {userHasAccess(user?.role, ['Admin', 'Support', 'Auditor']) && (
                <SpeedDialAction
                  key="Add Asset"
                  icon={<AddCircleOutlineIcon />}
                  tooltipTitle="Add New Asset"
                  onClick={() => {
                    setIsSpeedDialOpen(false);
                    setIsAddAssetModalOpen(true);
                  }}
                />
              )}

              <SpeedDialAction
                key="Generate CSV"
                icon={<FileDownloadIcon />}
                tooltipTitle="Generate CSV"
                onClick={() => {
                  setIsSpeedDialOpen(false);
                  setIsCsvModalOpen(true); // This now controls the modal
                }}
              />

              <SpeedDialAction
                key="Polling"
                icon={pollingEnabled ? <CircularProgress size={24} color="inherit" /> : <SyncIcon />}
                tooltipTitle={pollingEnabled ? 'Stop Polling' : 'Start Polling'}
                onClick={() => {
                  setPollingEnabled((prev) => !prev);
                }}
              />
            </SpeedDial>

            <Drawer
              anchor="bottom"
              open={isFilterDrawerOpen}
              onClose={() => setIsFilterDrawerOpen(false)}
            >
              <FiltersSidebar toggleSidebar={() => setIsFilterDrawerOpen(false)} />
            </Drawer>
          </>
        )}

        {/* MODALS - Pass state down to them now */}
        <GenerateCsvModal
          open={isCsvModalOpen}
          onClose={() => setIsCsvModalOpen(false)}
        />

        <AddAssetModal
          open={isAddAssetModalOpen}
          onClose={handleAddAssetModalClose}
        />
      </Box>
  );
};

export default Assets;
